import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { memoize } from 'lodash-es';

import type {
  SearchCriteriaFilterSchema,
  SearchSearchCriteriaSchema,
} from '@iconik-io/iconik-api/src/services/search';

import type { FacetsSchema } from '../common_list/types';
import type { CommonFiltersAppState, FacetValues, FilterValues } from './types';

const initialState: CommonFiltersAppState = {
  query: '',
  availableFacets: {},
  filterValues: {},
  facetValues: {},
  disabledFilters: [],
  unsupportedFilters: [],
  rawCriteria: undefined,
};

export const getCommonFiltersSlice = memoize(
  (filtersName: string, customInitialState?: CommonFiltersAppState) => {
    return createSlice({
      name: `commonFilters_${filtersName}`,
      initialState: customInitialState ?? initialState,
      reducers: {
        setQuery: (state, action: PayloadAction<string>) => {
          state.query = action.payload;
          state.rawCriteria = undefined;
        },
        setAvailableFacets: (state, action: PayloadAction<FacetsSchema>) => {
          state.availableFacets = action.payload;
        },
        setFilterValues: (state, action: PayloadAction<FilterValues>) => {
          state.filterValues = action.payload;
          state.rawCriteria = undefined;
        },
        addFilterValues: (state, action: PayloadAction<FilterValues>) => {
          state.filterValues = { ...state.filterValues, ...action.payload };
          state.rawCriteria = undefined;
        },
        removeFilterValues: (state, action: PayloadAction<string[]>) => {
          for (const key of action.payload) {
            delete state.filterValues[key];
          }
          state.disabledFilters = (state.disabledFilters ?? []).filter(
            (name) => !action.payload.includes(name),
          );
          state.rawCriteria = undefined;
        },
        setFilterDisabled: (
          state,
          action: PayloadAction<{ name: string; disabled: boolean }>,
        ) => {
          const { name, disabled } = action.payload;
          const current = new Set(state.disabledFilters ?? []);
          if (disabled) {
            current.add(name);
          } else {
            current.delete(name);
          }
          state.disabledFilters = Array.from(current);
          state.rawCriteria = undefined;
        },
        setDisabledFilters: (state, action: PayloadAction<string[]>) => {
          state.disabledFilters = action.payload;
          state.rawCriteria = undefined;
        },
        setUnsupportedFilters: (
          state,
          action: PayloadAction<SearchCriteriaFilterSchema[]>,
        ) => {
          state.unsupportedFilters = action.payload;
        },
        setRawCriteria: (
          state,
          action: PayloadAction<SearchSearchCriteriaSchema | undefined>,
        ) => {
          state.rawCriteria = action.payload;
        },
        setFacetValues: (state, action: PayloadAction<FacetValues>) => {
          state.facetValues = action.payload;
          state.rawCriteria = undefined;
        },
        resetFiltersData: (state) => {
          state.availableFacets = {};
          state.filterValues = {};
          state.facetValues = {};
          state.disabledFilters = [];
          state.unsupportedFilters = [];
          state.rawCriteria = undefined;
        },
      },
    });
  },
);

export type CommonFiltersActionTypes = ReturnType<
  typeof getCommonFiltersSlice
>['actions'];
